import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { NzSelectComponent } from "ng-zorro-antd/select";
import { HttpErrorResponse } from "@angular/common/http";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { NzModalService } from "ng-zorro-antd/modal";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { Router } from "@angular/router";
import { encodeRouteId } from "../../utils/base64_route_value.util";
import { Observable, Subject, Subscription, debounceTime, finalize, forkJoin, of, switchMap } from "rxjs";
import { AddUserLaunchService } from "../../services/add_user_launch.service";
import { AccountSettingsService } from "../../services/account_settings.service";
import { assessmentTaskSessionIdentity } from "../../services/assessment_task_session.util";
import { UserDialogOrigin } from "../../services/user_dialog_origin.util";
import { UserAccessError } from "../../services/assessment_user_access.util";
import { invitationResultMessage } from "../add_user_modal/invitation_result.util";
import {
    AssessmentTaskFormContext, AssessmentTaskFormLaunchService, AssessmentTaskFormLaunchState
} from "../../services/assessment_task_form_launch.service";
import { AssessmentTasksService } from "../../services/assessment_tasks.service";
import { AssessmentTaskInvalidationService } from "../../services/assessment_task_invalidation.service";
import type {
    AssessmentTaskCreationAssigneeOption, AssessmentTaskCreationOptions,
    AssessmentTaskDetail, AssessmentTaskEditInput, AssessmentTaskEditOptions,
    AssessmentTaskRequirement, AssessmentTaskRequirementRef, AssessmentTaskStatus
} from "../../services/assessment_tasks.types";
import { ProductLicensesService, TaskAssessmentChoice } from "../../services/product_licenses.service";
import { EvidenceRequirementsLaunchService } from "../../services/evidence_requirements_launch.service";
import { LoginService } from "../../services/login.service";
import { assessmentTaskStatusPresentation } from "../../utils/assessment_task_status.util";
import { calendarDateToPickerDate, isPickerDateDisabled, pickerDateToCalendarDate } from "../../utils/assessment_task_date.util";
import {
    TASK_ASSIGNEE_MAX_COUNT, TASK_DESCRIPTION_MAX_CODE_UNITS,
    TASK_REQUIREMENT_MAX_COUNT, taskDescriptionValidator, taskDueDateValidator,
    taskSelectionValidator
} from "../../utils/assessment_task_form_validation.util";

interface OptionPage<T> {
    items: T[];
    offset: number;
    total: number;
    search: string;
}

interface RequirementGroup {
    label: string;
    items: { key: string; label: string }[];
}

type RequirementLoadMode = "replace" | "preserve";

const ASSIGNEE_OPTION_LIMIT = 50;
const REQUIREMENT_OPTION_LIMIT = 500;
const ASSESSMENT_DISPLAY_LIMIT = 50;
const emptyPage = <T>(): OptionPage<T> => ({ items: [], offset: 0, total: 0, search: "" });
const requirementKey = (item: { sectionKey: string; requirementKey: string }): string =>
    JSON.stringify([item.sectionKey, item.requirementKey]);
const positiveId = (value: unknown): value is number =>
    typeof value === "number" && Number.isSafeInteger(value) && value > 0;

function optionSearch(value: unknown): string {
    return (typeof value === "string" ? value : "").slice(0, 100)
        .replace(/[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g, "");
}

function sameValues<T>(left: readonly T[], right: readonly T[]): boolean {
    if (left.length !== right.length) return false;
    const expected = new Set(right);
    return left.every((value) => expected.has(value));
}

@Component({
    selector: "app-assessment-task-form-modal",
    templateUrl: "./assessment_task_form_modal.component.html",
    styleUrls: ["./assessment_task_form_modal.component.scss"]
})
export class AssessmentTaskFormModalComponent implements OnInit, OnDestroy {
    state: AssessmentTaskFormLaunchState;
    readonly descriptionMaxLength = TASK_DESCRIPTION_MAX_CODE_UNITS;
    readonly statuses: AssessmentTaskStatus[] = ["needs_review", "pending_review", "rejected", "approved"];
    readonly statusPresentation = assessmentTaskStatusPresentation;
    readonly form = new FormGroup({
        productLicenseId: new FormControl<number | null>(null, Validators.required),
        requirementKeys: new FormControl<string[]>([], {
            nonNullable: true, validators: taskSelectionValidator(TASK_REQUIREMENT_MAX_COUNT, true)
        }),
        assigneeUserIds: new FormControl<number[]>([], {
            nonNullable: true, validators: taskSelectionValidator(TASK_ASSIGNEE_MAX_COUNT)
        }),
        description: new FormControl<string>("", { nonNullable: true, validators: taskDescriptionValidator }),
        dueDate: new FormControl<string | null>(null, taskDueDateValidator(() => this.originalDueDate)),
        status: new FormControl<AssessmentTaskStatus>("needs_review", { nonNullable: true })
    });
    requirementPage = emptyPage<AssessmentTaskRequirement>();
    assigneePage = emptyPage<AssessmentTaskCreationAssigneeOption>();
    optionsLoading = false;
    optionsError: string | null = null;
    assessmentsLoading = false;
    assessmentsError: string | null = null;
    assessmentSearch = "";
    editTask: AssessmentTaskDetail | null = null;
    editLoading = false;
    editError: string | null = null;
    saving = false;
    saveError: string | null = null;
    saveConflict = false;
    initialLoadPending = false;
    requiredEvidenceAvailable = false;
    requiredEvidenceLaunchPending = false;
    dueDatePickerValue: Date | null = null;
    canInviteAssessmentUser = false;
    private assignmentScopeRefreshPending = false;
    childUserDialogOpen = false;
    newUserReturnPending = false;
    assigneeDropdownOpen = false;
    @ViewChild("taskFormRoot") private taskFormRoot?: ElementRef<HTMLElement>;
    @ViewChild("assigneeSelect") private assigneeSelect?: NzSelectComponent;
    private readonly userOriginChanges = new Subject<void>();
    private formRevision = 0;
    private invitationCapabilityRequest: Subscription | null = null;
    private childUserRequest: Subscription | null = null;
    private returnedUserRequest: Subscription | null = null;

    private readonly subscriptions = new Subscription();
    private readonly assigneeSearchChanges = new Subject<{ search: string; scope: number }>();
    private readonly selectedRequirements = new Map<string, AssessmentTaskRequirement>();
    private readonly selectedAssignees = new Map<number, AssessmentTaskCreationAssigneeOption>();
    private licenses: TaskAssessmentChoice[] = [];
    private optionsRequest: Subscription | null = null;
    private licensesRequest: Subscription | null = null;
    private detailRequest: Subscription | null = null;
    private saveRequest: Subscription | null = null;
    private requiredEvidenceAccessRequest: Subscription | null = null;
    private requiredEvidenceLaunchRequest: Subscription | null = null;
    private openTimer: ReturnType<typeof setTimeout> | null = null;
    private scopeVersion = 0;
    private optionVersion = 0;
    private licenseVersion = 0;
    private originalDueDate: string | null = null;
    private originalDescription = "";
    private originalRequirementKeys: string[] = [];
    private originalAssigneeIds: number[] = [];
    private assessmentChoicesCache: {
        licenses: TaskAssessmentChoice[]; search: string; selectedId: number | null;
        items: TaskAssessmentChoice[];
    } | null = null;
    private requirementGroupsCache: {
        pageItems: AssessmentTaskRequirement[]; selectedKeys: string[]; search: string;
        groups: RequirementGroup[];
    } | null = null;
    private assigneeChoicesCache: {
        pageItems: AssessmentTaskCreationAssigneeOption[]; selectedIds: number[];
        items: AssessmentTaskCreationAssigneeOption[];
    } | null = null;

    constructor(
        private readonly launch: AssessmentTaskFormLaunchService,
        private readonly tasks: AssessmentTasksService,
        private readonly taskInvalidations: AssessmentTaskInvalidationService,
        private readonly productLicenses: ProductLicensesService,
        private readonly evidenceLauncher: EvidenceRequirementsLaunchService,
        private readonly login: LoginService,
        private readonly modal: NzModalService,
        private readonly notification: NzNotificationService,
        private readonly router: Router,
        private readonly accountSettings: AccountSettingsService,
        private readonly addUserLauncher: AddUserLaunchService
    ) {
        // The root component can mount after an entry point has already opened the form.
        this.state = { isOpen: false, context: null };
    }

    ngOnInit(): void {
        let sessionToken = this.login.getToken();
        let sessionIdentity = assessmentTaskSessionIdentity(this.login.getUserSnapshot());
        this.subscriptions.add(this.login.getUserUpdates().subscribe(() => {
            const nextToken = this.login.getToken();
            const nextIdentity = assessmentTaskSessionIdentity(this.login.getUserSnapshot());
            if ((nextToken !== sessionToken || nextIdentity !== sessionIdentity) && this.state.isOpen) this.launch.close();
            sessionToken = nextToken;
            sessionIdentity = nextIdentity;
        }));
        this.subscriptions.add(this.form.valueChanges.subscribe(() => {
            this.formRevision += 1;
            this.userOriginChanges.next();
        }));
        this.subscriptions.add(this.launch.watchState().subscribe((state) => {
            if (state.context === this.state.context && state.isOpen === this.state.isOpen) return;
            this.state = state;
            this.resetScope();
            if (state.isOpen && state.context) {
                this.initialLoadPending = true;
                const scope = this.scopeVersion;
                const context = state.context;
                // Paint the modal's small loading view before starting assessment and option reads.
                this.openTimer = setTimeout(() => {
                    this.openTimer = null;
                    if (scope === this.scopeVersion && this.state.context === context) {
                        this.initialize(context);
                    }
                }, 0);
            }
        }));
        this.subscriptions.add(this.form.controls.productLicenseId.valueChanges.subscribe((id) => {
            if (!this.state.isOpen || this.state.context?.kind !== "create" ||
                this.state.context.productLicenseId !== null) return;
            this.cancelOptionsRequest();
            this.requirementPage = emptyPage();
            this.assigneePage = emptyPage();
            this.selectedRequirements.clear();
            this.selectedAssignees.clear();
            this.form.controls.requirementKeys.setValue([]);
            this.form.controls.assigneeUserIds.setValue([]);
            if (id) this.loadOptions();
        }));
        this.subscriptions.add(this.form.controls.requirementKeys.valueChanges.subscribe((keys) => {
            this.requirementSelectionChanged(keys);
        }));
        this.subscriptions.add(this.form.controls.assigneeUserIds.valueChanges.subscribe((ids) => {
            this.assigneeSelectionChanged(ids);
        }));
        this.subscriptions.add(this.assigneeSearchChanges.pipe(debounceTime(250)).subscribe(({ search, scope }) => {
            if (scope === this.scopeVersion && this.state.isOpen && search === this.assigneePage.search) {
                this.loadOptions("preserve");
            }
        }));
        this.subscriptions.add(this.taskInvalidations.watchInvalidations().subscribe((event) => {
            if (event.kind !== "scope" || !this.state.isOpen) return;
            const selectedLicenseId = this.form.controls.productLicenseId.getRawValue();
            if (event.productLicenseId !== null && event.productLicenseId !== selectedLicenseId) return;
            this.assignmentScopeRefreshPending = true;
            this.refreshAssignmentScopeWhenReady();
        }));
    }

    private refreshAssignmentScopeWhenReady(): void {
        if (!this.assignmentScopeRefreshPending || !this.state.isOpen || this.saving ||
            this.childUserDialogOpen || this.newUserReturnPending) return;
        this.assignmentScopeRefreshPending = false;
        const license = this.form.controls.productLicenseId.getRawValue();
        if (license) this.loadOptions("preserve");
        else this.loadAssessments();
        const context = this.state.context;
        if (context?.kind === "create" && context.productLicenseId !== null && context.requirement === null) {
            this.loadRequiredEvidenceAccess(context);
        }
    }

    ngOnDestroy(): void {
        this.userOriginChanges.complete();
        this.resetScope();
        this.subscriptions.unsubscribe();
    }

    get isAssessmentLocked(): boolean {
        return !!this.state.context && this.state.context.productLicenseId !== null;
    }

    get isEvidenceModalOpen(): boolean {
        return this.evidenceLauncher.getSnapshot().isOpen;
    }

    get isRequirementLocked(): boolean {
        return this.state.context?.kind === "create" && this.state.context.requirement !== null;
    }

    get modalTitle(): string {
        if (this.state.context?.kind === "edit") return "Edit Task";
        if (this.isRequirementLocked && this.isAssessmentLocked) return "Assign User to this Requirement/Control";
        return this.isAssessmentLocked ? "Add Task to this Assessment" : "Add Task";
    }

    readonly dueDateDisabled = (date: Date): boolean => {
        return isPickerDateDisabled(date, this.originalDueDate);
    };

    changeDueDate(date: Date | null): void {
        const dueDate = pickerDateToCalendarDate(date);
        this.dueDatePickerValue = dueDate ? date : null;
        this.form.controls.dueDate.setValue(dueDate);
        this.form.controls.dueDate.markAsTouched();
    }

    get hasEditChanges(): boolean {
        if (!this.editTask) return false;
        const value = this.form.getRawValue();
        return value.description !== this.originalDescription || value.dueDate !== this.originalDueDate ||
            !sameValues(value.requirementKeys, this.originalRequirementKeys) ||
            !sameValues(value.assigneeUserIds, this.originalAssigneeIds) || value.status !== this.editTask.status;
    }

    get canSave(): boolean {
        return this.state.isOpen && !this.assignmentScopeRefreshPending && !this.childUserDialogOpen && !this.newUserReturnPending && !this.saving && !this.saveConflict && !this.optionsLoading && !this.optionsError &&
            this.form.valid && (this.state.context?.kind === "create" ||
                (!!this.editTask && this.hasEditChanges));
    }

    get showRequiredEvidence(): boolean {
        const context = this.state.context;
        return context?.kind === "create" && context.requirement === null &&
            positiveId(context.productLicenseId) && this.requiredEvidenceAvailable;
    }

    get canOpenRequiredEvidence(): boolean {
        return this.showRequiredEvidence && !this.childUserDialogOpen && !this.newUserReturnPending && !this.saving && !this.initialLoadPending &&
            !this.optionsLoading && !this.requiredEvidenceLaunchPending;
    }

    get showTaskFields(): boolean {
        return (this.state.context?.kind === "create" && !!this.form.controls.productLicenseId.getRawValue()) ||
            (this.state.context?.kind === "edit" && !!this.editTask);
    }

    get isStatusOnlyEdit(): boolean {
        return this.state.context?.kind === "edit" && this.editTask?.capabilities.canEditTask !== true;
    }

    get displayedAssessments(): TaskAssessmentChoice[] {
        const search = this.assessmentSearch.trim().toLowerCase();
        const selectedId = this.form.controls.productLicenseId.value;
        const cached = this.assessmentChoicesCache;
        if (cached && cached.licenses === this.licenses && cached.search === search &&
            cached.selectedId === selectedId) return cached.items;
        const selected = this.licenses.find((item) => item.id === selectedId);
        const matches = this.licenses.filter((item) => item.id !== selectedId &&
            (!search || this.assessmentLabel(item).toLowerCase().includes(search)));
        const items = [...(selected ? [selected] : []),
            ...matches.slice(0, ASSESSMENT_DISPLAY_LIMIT - (selected ? 1 : 0))];
        this.assessmentChoicesCache = { licenses: this.licenses, search, selectedId, items };
        return items;
    }

    get requirementGroups(): RequirementGroup[] {
        const pageItems = this.requirementPage.items;
        const selectedKeys = this.form.controls.requirementKeys.value;
        const search = this.requirementPage.search.trim().toLowerCase();
        const cached = this.requirementGroupsCache;
        if (cached && cached.pageItems === pageItems && cached.selectedKeys === selectedKeys &&
            cached.search === search) {
            return cached.groups;
        }
        const matches = search ? pageItems.filter((item) =>
            `${item.sectionKey} ${item.sectionLabel} ${item.requirementKey} ${item.requirementLabel}`
                .toLowerCase().includes(search)) : pageItems;
        const current = new Map(matches.map((item) => [requirementKey(item), item]));
        for (const key of selectedKeys) {
            const selected = this.selectedRequirements.get(key);
            if (selected) current.set(key, selected);
        }
        const groups = new Map<string, RequirementGroup>();
        for (const [key, item] of current) {
            const label = item.sectionLabel || item.sectionKey;
            if (!groups.has(label)) groups.set(label, { label, items: [] });
            const requirementLabel = item.requirementLabel || item.requirementKey;
            const fullNumberSection = item.sectionKey === "section_7" || item.sectionKey === "section_2b";
            const optionLabel = fullNumberSection && !requirementLabel.startsWith(item.requirementKey)
                ? `${item.requirementKey} — ${requirementLabel}` : requirementLabel;
            groups.get(label)!.items.push({ key, label: optionLabel });
        }
        const result = [...groups.values()];
        this.requirementGroupsCache = { pageItems, selectedKeys, search, groups: result };
        return result;
    }

    get displayedAssignees(): AssessmentTaskCreationAssigneeOption[] {
        const pageItems = this.assigneePage.items;
        const selectedIds = this.form.controls.assigneeUserIds.value;
        const cached = this.assigneeChoicesCache;
        if (cached && cached.pageItems === pageItems && cached.selectedIds === selectedIds) {
            return cached.items;
        }
        const current = new Map(pageItems.map((item) => [item.userId, item]));
        for (const id of selectedIds) {
            const selected = this.selectedAssignees.get(id);
            if (selected) current.set(id, selected);
        }
        const items = [...current.values()];
        this.assigneeChoicesCache = { pageItems, selectedIds, items };
        return items;
    }

    assessmentLabel(license: TaskAssessmentChoice): string {
        return license.displayName || `${license.merchantName || "Assessment"} · ${license.formType} #${license.id}`;
    }

    searchAssessments(value: string): void {
        this.assessmentSearch = optionSearch(value);
    }

    searchRequirements(value: string): void {
        const search = optionSearch(value);
        if (search === this.requirementPage.search) return;
        this.requirementPage = { ...this.requirementPage, search };
    }

    searchAssignees(value: string): void {
        if (this.childUserDialogOpen || this.newUserReturnPending) return;
        const search = optionSearch(value);
        if (search === this.assigneePage.search) return;
        this.cancelOptionsRequest();
        this.assigneePage = { ...emptyPage<AssessmentTaskCreationAssigneeOption>(), search };
        this.assigneeSearchChanges.next({ search, scope: this.scopeVersion });
    }

    requirementSelectionChanged(keys: string[]): void {
        if (this.isRequirementLocked) return;
        for (const item of this.requirementPage.items) {
            if (keys.includes(requirementKey(item))) this.selectedRequirements.set(requirementKey(item), item);
        }
        for (const key of this.selectedRequirements.keys()) {
            if (!keys.includes(key)) this.selectedRequirements.delete(key);
        }
    }

    assigneeSelectionChanged(ids: number[]): void {
        for (const item of this.assigneePage.items) {
            if (ids.includes(item.userId)) this.selectedAssignees.set(item.userId, item);
        }
        for (const id of this.selectedAssignees.keys()) {
            if (!ids.includes(id)) this.selectedAssignees.delete(id);
        }
    }

    changeAssigneePage(direction: -1 | 1): void {
        const next = this.assigneePage.offset + direction * ASSIGNEE_OPTION_LIMIT;
        if (next < 0 || next >= this.assigneePage.total || this.optionsLoading) return;
        this.assigneePage = { ...this.assigneePage, offset: next };
        this.loadOptions("preserve");
    }

    retryOptions(): void {
        this.loadOptions();
    }

    retryAssessments(): void {
        this.loadAssessments();
    }

    reloadEditTask(): void {
        const context = this.state.context;
        if (context?.kind !== "edit" || this.saving || this.childUserDialogOpen || this.newUserReturnPending) return;
        if (!this.form.dirty) {
            this.loadEditTask(context);
            return;
        }
        this.modal.confirm({
            nzTitle: "Reload task?",
            nzContent: "This will discard your unsaved changes and load the latest task.",
            nzOkText: "Reload task",
            nzOnOk: () => this.loadEditTask(context)
        });
    }

    save(): void {
        if (!this.state.isOpen || this.assignmentScopeRefreshPending || this.childUserDialogOpen || this.newUserReturnPending || this.saving || this.saveConflict || this.optionsLoading || this.optionsError) return;
        this.form.controls.dueDate.updateValueAndValidity({ emitEvent: false });
        this.form.markAllAsTouched();
        if (this.form.invalid) return;
        const value = this.form.getRawValue();
        const requirements = this.selectedRequirementRefs(value.requirementKeys);
        if (!requirements || requirements.length !== value.requirementKeys.length) {
            this.saveError = "Reload the requirement choices before saving.";
            return;
        }
        const scope = this.scopeVersion;
        const context = this.state.context;
        if (!context) return;
        this.saving = true;
        this.saveError = null;
        this.saveConflict = false;
        if (context.kind === "create") {
            const productLicenseId = value.productLicenseId;
            if (typeof productLicenseId !== "number" || !Number.isSafeInteger(productLicenseId) ||
                productLicenseId <= 0 ||
                context.productLicenseId !== null &&
                context.productLicenseId !== productLicenseId) {
                this.saving = false;
                this.saveError = "Select an available assessment.";
                return;
            }
            if (context.requirement &&
                (requirements.length !== 1 || requirementKey(requirements[0]) !== requirementKey(context.requirement))) {
                this.saving = false;
                this.saveError = "The selected requirement changed. Reopen the task form.";
                return;
            }
            this.saveRequest = this.tasks.createTask({
                productLicenseId, description: value.description, dueDate: value.dueDate,
                requirements, assigneeUserIds: value.assigneeUserIds, status: value.status
            }).subscribe({
                next: () => {
                    if (scope === this.scopeVersion) {
                        this.closeAfterSave();
                        this.notification.success("Task added", "The task assignment was created.", {
                            nzPlacement: "topRight"
                        });
                    }
                },
                error: (error) => this.handleSaveError(error, scope)
            });
            return;
        }
        const task = this.editTask;
        if (!task || task.id !== context.taskId || task.productLicenseId !== context.productLicenseId ||
            !(task.capabilities.canEditTask || task.capabilities.canChangeStatus) || !this.hasEditChanges) {
            this.saving = false;
            return;
        }
        const changes: AssessmentTaskEditInput = { revision: task.revision };
        if (value.description !== this.originalDescription) changes.description = value.description;
        if (value.dueDate !== this.originalDueDate) changes.dueDate = value.dueDate;
        if (!sameValues(value.requirementKeys, this.originalRequirementKeys)) changes.requirements = requirements;
        if (!sameValues(value.assigneeUserIds, this.originalAssigneeIds)) {
            changes.assigneeUserIds = value.assigneeUserIds;
        }
        const statusChanged = value.status !== task.status;
        if (!task.capabilities.canEditTask && Object.keys(changes).length > 1) {
            this.saving = false; this.saveError = "You may update only this task's status."; return;
        }
        const structuralChanged = Object.keys(changes).length > 1;
        let structuralSaved = false;
        const request = structuralChanged
            ? this.tasks.editTask(task.id, changes, task).pipe(switchMap((receipt) => {
                structuralSaved = true;
                return statusChanged
                    ? this.tasks.changeTaskStatus(task.id, value.status, receipt.revision, receipt.task ?? task)
                    : of(receipt);
            }))
            : this.tasks.changeTaskStatus(task.id, value.status, task.revision, task);
        this.saveRequest = request.subscribe({
            next: () => { if (scope === this.scopeVersion) this.closeAfterSave(); },
            error: (error) => {
                if (structuralSaved && scope === this.scopeVersion && this.state.isOpen) {
                    this.saving = false;
                    this.saveConflict = true;
                    this.saveError = "The other task changes were saved, but the status could not be confirmed. Reload the task to see its current state.";
                } else {
                    this.handleSaveError(error, scope);
                }
            }
        });
    }

    close(): void {
        if (this.saving || this.childUserDialogOpen || this.newUserReturnPending) return;
        this.launch.close();
    }

    openAssessmentAssignments(event?: Event): void {
        event?.preventDefault(); event?.stopPropagation();
        if (this.saving || this.childUserDialogOpen || this.newUserReturnPending) return;
        const context = this.state.context;
        if (!this.state.isOpen || !context) return;
        const scope = this.scopeVersion;
        const token = this.login.getToken();
        const identity = assessmentTaskSessionIdentity(this.login.getUserSnapshot());
        if (!token || !identity) return;
        const assessmentId = context.kind === "edit"
            ? this.editTask?.productLicenseId
            : this.form.controls.productLicenseId.getRawValue();
        if (typeof assessmentId !== "number" || !Number.isSafeInteger(assessmentId) || assessmentId <= 0) {
            this.notification.warning("Select an assessment", "Choose an assessment before opening its assignments.", {
                nzPlacement: "topRight"
            });
            return;
        }
        // Open during the click so the asynchronous scope read cannot trigger popup blocking.
        const tab = window.open("", "_blank");
        if (!tab) {
            this.notification.warning("New tab blocked", "Allow pop-ups for this site to open assessment assignees.");
            return;
        }
        tab.opener = null;
        this.productLicenses.loadTaskAssessmentChoices().subscribe({
            next: (choices) => {
                if (scope !== this.scopeVersion || !this.state.isOpen || this.state.context !== context || this.saving ||
                    this.childUserDialogOpen || this.newUserReturnPending ||
                    token !== this.login.getToken() || identity !== assessmentTaskSessionIdentity(this.login.getUserSnapshot()) ||
                    assessmentId !== (context.kind === "edit" ? this.editTask?.productLicenseId : this.form.controls.productLicenseId.getRawValue())) {
                    tab.close(); return;
                }
                const choice = choices.find((item) => item.id === assessmentId);
                if (!choice) {
                    tab.close();
                    this.notification.error("Assessment unavailable", "Unable to open assignments for this assessment.", {
                        nzPlacement: "topRight"
                    });
                    return;
                }
                tab.location.replace(this.router.serializeUrl(this.router.createUrlTree(
                    ["/merchants", "assign_users", encodeRouteId(choice.merchantId), encodeRouteId(choice.id)])));
            },
            error: () => {
                tab.close();
                this.notification.error("Unable to load assessment", "Try opening assignments again.", { nzPlacement: "topRight" });
            }
        });
    }

    openRequiredEvidence(): void {
        const context = this.state.context;
        if (!this.canOpenRequiredEvidence || context?.kind !== "create" ||
            context.requirement !== null || !positiveId(context.productLicenseId)) return;

        const scope = this.scopeVersion;
        const accountId = this.login.getCurrentAccountId();
        const userId = this.login.getCurrentUserId();
        const token = this.login.getToken();
        if (!positiveId(accountId) || !positiveId(userId) || !token) return;
        const assessmentId = context.productLicenseId;
        this.requiredEvidenceLaunchPending = true;
        this.requiredEvidenceLaunchRequest = this.productLicenses.loadTaskAssessmentChoices().subscribe({
            next: (choices) => {
                if (!this.isCurrentEvidenceContext(context, scope, accountId, userId, token)) return;
                this.requiredEvidenceLaunchPending = false;
                if (!choices.some((choice) => choice.id === assessmentId)) {
                    this.requiredEvidenceAvailable = false;
                    this.showRequiredEvidenceUnavailable();
                    return;
                }
                if (!this.canOpenRequiredEvidence) return;
                this.evidenceLauncher.openAssessment(assessmentId);
            },
            error: () => {
                if (!this.isCurrentEvidenceContext(context, scope, accountId, userId, token)) return;
                this.requiredEvidenceLaunchPending = false;
                if (!this.canOpenRequiredEvidence) return;
                this.showRequiredEvidenceUnavailable();
            }
        });
    }

    get canLaunchAddUser(): boolean {
        return this.state.isOpen && this.showTaskFields && this.canInviteAssessmentUser &&
            !this.saving && !this.initialLoadPending && !this.optionsLoading && !this.optionsError &&
            !this.childUserDialogOpen && !this.newUserReturnPending;
    }

    private refreshInvitationCapability(): void {
        this.invitationCapabilityRequest?.unsubscribe();
        this.canInviteAssessmentUser = false;
        const licenseId = this.form.controls.productLicenseId.getRawValue(), scope = this.scopeVersion;
        const token = this.login.getToken(), identity = assessmentTaskSessionIdentity(this.login.getUserSnapshot());
        if (!positiveId(licenseId) || !this.state.isOpen || !token || !identity) return;
        this.invitationCapabilityRequest = this.accountSettings.getUserAccess({ productLicenseId: licenseId }).subscribe({
            next: (access) => {
                if (scope !== this.scopeVersion || this.form.controls.productLicenseId.getRawValue() !== licenseId ||
                    token !== this.login.getToken() || identity !== assessmentTaskSessionIdentity(this.login.getUserSnapshot())) return;
                this.canInviteAssessmentUser = access.assessment?.productLicenseId === licenseId && access.capabilities.canInviteOnAssessment;
            }, error: () => {
                if (scope === this.scopeVersion && this.form.controls.productLicenseId.getRawValue() === licenseId) this.canInviteAssessmentUser = false;
            }
        });
    }

    openAddUser(event: Event): void {
        event.preventDefault(); event.stopPropagation();
        const context = this.state.context, licenseId = this.form.controls.productLicenseId.getRawValue();
        const root = this.taskFormRoot?.nativeElement;
        if (!this.canLaunchAddUser || !context || !positiveId(licenseId) || !root) return;
        const scope = this.scopeVersion, revision = this.formRevision;
        const token = this.login.getToken(), identity = assessmentTaskSessionIdentity(this.login.getUserSnapshot());
        const current = () => this.state.isOpen && this.state.context === context && scope === this.scopeVersion &&
            licenseId === this.form.controls.productLicenseId.getRawValue() && revision === this.formRevision &&
            token === this.login.getToken() && identity === assessmentTaskSessionIdentity(this.login.getUserSnapshot());
        const origin: UserDialogOrigin = {
            snapshot: () => ({ instance: this.state.context ?? this, isOpen: this.state.isOpen, revision: this.formRevision,
                productLicenseId: this.form.controls.productLicenseId.getRawValue() ?? 0, field: "assigneeUserIds" }),
            changes: this.userOriginChanges, element: root, launchEvent: event,
            closeDropdown: () => { this.assigneeDropdownOpen = false; }, focus: () => this.assigneeSelect?.focus()
        };
        this.childUserDialogOpen = true;
        this.childUserRequest = this.addUserLauncher.openAssessment(licenseId, undefined, origin).pipe(finalize(() => {
            if (scope === this.scopeVersion) { this.childUserDialogOpen = false; this.refreshAssignmentScopeWhenReady(); }
        })).subscribe({
            next: (result) => {
                if (!current() || result.outcome === "canceled") return;
                if (result.outcome === "storage_user_converted" || result.productLicenseId !== licenseId) return;
                if (result.outcome === "delivery_failed" || result.outcome === "delivery_unknown") {
                    this.notification.warning("User saved", invitationResultMessage(result));
                }
                this.reconcileReturnedUser(result.user.id, context, licenseId, scope, revision, token, identity);
            },
            error: (error: unknown) => {
                if (!current()) return;
                this.canInviteAssessmentUser = false;
                this.saveError = error instanceof UserAccessError ? error.message : "Unable to add a user. Your task draft is unchanged; reload current permissions and try again.";
            }
        });
    }

    private reconcileReturnedUser(userId: number, context: AssessmentTaskFormContext, licenseId: number,
        scope: number, revision: number, token: string | null, identity: string | null): void {
        this.returnedUserRequest?.unsubscribe();
        this.cancelOptionsRequest();
        this.optionsLoading = false;
        this.newUserReturnPending = true;
        const sameContext = () => this.state.isOpen && this.state.context === context && scope === this.scopeVersion &&
            this.form.controls.productLicenseId.getRawValue() === licenseId && token === this.login.getToken() &&
            identity === assessmentTaskSessionIdentity(this.login.getUserSnapshot());
        const query = { assigneeUserId: userId, assigneeOffset: 0, assigneeLimit: 1, requirementLimit: 1 };
        const request: Observable<AssessmentTaskCreationOptions | AssessmentTaskEditOptions> = context.kind === "edit"
            ? this.tasks.getEditOptions(context.taskId, query) : this.tasks.getCreationOptions(licenseId, query);
        this.returnedUserRequest = request.pipe(finalize(() => {
            if (sameContext()) { this.newUserReturnPending = false; this.refreshAssignmentScopeWhenReady(); }
        })).subscribe({
            next: (options) => {
                if (!sameContext() || revision !== this.formRevision) return;
                const member = options.assignees.items.find((item) => item.userId === userId);
                if (options.productLicenseId !== licenseId || !member || options.assignees.total !== 1 ||
                    context.kind === "edit" && (!("canEdit" in options) || !options.canEdit || options.revision !== this.editTask?.revision)) {
                    this.optionsError = "The user was saved, but task access or eligibility changed. Your draft is preserved. Reload current task choices before selecting this user.";
                    return;
                }
                const ids = this.form.controls.assigneeUserIds.value;
                if (!ids.includes(userId) && ids.length >= TASK_ASSIGNEE_MAX_COUNT) {
                    this.saveError = "The user was saved. Remove an existing task assignee before adding another user.";
                    return;
                }
                this.selectedAssignees.set(userId, member);
                this.form.controls.assigneeUserIds.setValue([...new Set([...ids, userId])]);
                this.form.controls.assigneeUserIds.markAsDirty();
                this.assigneeChoicesCache = null;
                this.saveError = null;
                this.loadOptions("preserve");
            }, error: () => {
                if (!sameContext() || revision !== this.formRevision) return;
                this.canInviteAssessmentUser = false;
                this.optionsError = "The user was saved, but current task access could not be confirmed. Your draft is preserved. Reload task choices to recheck access.";
            }
        });
    }

    private initialize(context: AssessmentTaskFormContext): void {
        if (context.productLicenseId !== null) {
            this.form.controls.productLicenseId.setValue(context.productLicenseId, { emitEvent: false });
            this.form.controls.productLicenseId.disable({ emitEvent: false });
        }
        if (context.kind === "create" && context.productLicenseId === null) {
            this.loadAssessments();
        }
        if (context.kind === "create" && context.requirement) {
            const key = requirementKey(context.requirement);
            this.form.controls.requirementKeys.setValue([key], { emitEvent: false });
            this.form.controls.requirementKeys.disable({ emitEvent: false });
            this.selectedRequirements.set(key, {
                ...context.requirement,
                sectionLabel: context.requirement.sectionKey,
                requirementLabel: context.requirement.requirementKey,
                navigationSectionKey: context.requirement.sectionKey,
                navigationRequirementKey: context.requirement.requirementKey
            });
        }
        if (context.kind === "create" && context.productLicenseId !== null) {
            this.loadOptions();
            if (context.requirement === null) this.loadRequiredEvidenceAccess(context);
        }
        if (context.kind === "edit") this.loadEditTask(context);
    }

    private loadRequiredEvidenceAccess(
        context: Extract<AssessmentTaskFormContext, { kind: "create" }>
    ): void {
        this.requiredEvidenceAccessRequest?.unsubscribe();
        this.requiredEvidenceAvailable = false;
        if (!positiveId(context.productLicenseId) || context.requirement !== null) return;
        const scope = this.scopeVersion;
        const accountId = this.login.getCurrentAccountId();
        const userId = this.login.getCurrentUserId();
        const token = this.login.getToken();
        if (!positiveId(accountId) || !positiveId(userId) || !token) return;
        this.requiredEvidenceAccessRequest = this.productLicenses.loadTaskAssessmentChoices().subscribe({
            next: (choices) => {
                if (!this.isCurrentEvidenceContext(context, scope, accountId, userId, token)) return;
                this.requiredEvidenceAvailable = choices.some((choice) => choice.id === context.productLicenseId);
            },
            error: () => {
                if (this.isCurrentEvidenceContext(context, scope, accountId, userId, token)) {
                    this.requiredEvidenceAvailable = false;
                }
            }
        });
    }

    private isCurrentEvidenceContext(
        context: AssessmentTaskFormContext, scope: number, accountId: number, userId: number, token: string
    ): boolean {
        return this.state.isOpen && this.state.context === context && scope === this.scopeVersion &&
            this.login.getCurrentAccountId() === accountId && this.login.getCurrentUserId() === userId &&
            this.login.getToken() === token;
    }

    private showRequiredEvidenceUnavailable(): void {
        this.notification.error("Evidence unavailable", "This assessment is unavailable in your current scope.", {
            nzPlacement: "topRight"
        });
    }

    private loadEditTask(context: Extract<AssessmentTaskFormContext, { kind: "edit" }>): void {
        if (!this.state.isOpen || this.state.context !== context) return;
        this.detailRequest?.unsubscribe();
        this.cancelOptionsRequest();
        const scope = this.scopeVersion;
        this.editTask = null;
        this.editLoading = true;
        this.editError = null;
        this.optionsError = null;
        this.saveError = null;
        this.saveConflict = false;
        this.selectedRequirements.clear();
        this.selectedAssignees.clear();
        this.requirementPage = emptyPage();
        this.assigneePage = emptyPage();
        this.form.reset({
            productLicenseId: context.productLicenseId, requirementKeys: [], assigneeUserIds: [],
            description: "", dueDate: null, status: "needs_review"
        }, { emitEvent: false });
        this.dueDatePickerValue = null;
        this.detailRequest = this.tasks.getTask(context.taskId).subscribe({
            next: (task) => {
                if (scope !== this.scopeVersion || this.state.context !== context) return;
                this.editLoading = false;
                if (task.productLicenseId !== context.productLicenseId || !(task.capabilities.canEditTask || task.capabilities.canChangeStatus)) {
                    this.editError = "This task is unavailable for editing.";
                    this.maybeFinishInitialLoad();
                    return;
                }
                this.editTask = task;
                this.originalDescription = task.description;
                this.originalDueDate = task.dueDate;
                this.originalRequirementKeys = task.requirements.map(requirementKey);
                this.originalAssigneeIds = task.assignees.map((item) => item.userId);
                this.form.controls.assigneeUserIds.setValidators((control) => {
                    const ids: unknown = control.value;
                    return Array.isArray(ids) && sameValues(ids, this.originalAssigneeIds)
                        ? null : taskSelectionValidator(TASK_ASSIGNEE_MAX_COUNT)(control);
                });
                for (const item of task.requirements) this.selectedRequirements.set(requirementKey(item), item);
                for (const item of task.assignees) {
                    this.selectedAssignees.set(item.userId, { userId: item.userId, displayName: item.displayName });
                }
                this.form.patchValue({
                    requirementKeys: [...this.originalRequirementKeys],
                    assigneeUserIds: [...this.originalAssigneeIds],
                    description: task.description, dueDate: task.dueDate, status: task.status
                }, { emitEvent: false });
                this.dueDatePickerValue = calendarDateToPickerDate(task.dueDate);
                for (const key of ["description", "dueDate", "assigneeUserIds", "requirementKeys"] as const) {
                    if (!task.capabilities.canEditTask) this.form.controls[key].disable({ emitEvent: false });
                    else this.form.controls[key].enable({ emitEvent: false });
                }
                this.form.markAsPristine();
                this.form.controls.dueDate.updateValueAndValidity({ emitEvent: false });
                this.loadOptions();
                this.maybeFinishInitialLoad();
            },
            error: () => {
                if (scope !== this.scopeVersion || this.state.context !== context) return;
                this.editLoading = false;
                this.editError = "Unable to load this task for editing.";
                this.maybeFinishInitialLoad();
            }
        });
    }

    private loadAssessments(): void {
        this.licensesRequest?.unsubscribe();
        const scope = this.scopeVersion;
        const version = ++this.licenseVersion;
        this.assessmentsLoading = true;
        this.assessmentsError = null;
        this.licensesRequest = this.productLicenses.loadTaskAssessmentChoices().subscribe({
            next: (licenses) => {
                if (scope !== this.scopeVersion || version !== this.licenseVersion || !this.state.isOpen) return;
                this.licenses = this.state.context?.kind === "edit" ? licenses
                    : licenses.filter(license => license.canCreateTask === true);
                this.assessmentsLoading = false;
                this.maybeFinishInitialLoad();
            },
            error: () => {
                if (scope !== this.scopeVersion || version !== this.licenseVersion || !this.state.isOpen) return;
                this.assessmentsLoading = false;
                this.assessmentsError = "Unable to load assessments.";
                this.maybeFinishInitialLoad();
            }
        });
    }

    private loadOptions(mode: RequirementLoadMode = "replace"): void {
        this.canInviteAssessmentUser = false;
        const assessmentId = this.form.controls.productLicenseId.getRawValue();
        const context = this.state.context;
        if (!this.state.isOpen || !assessmentId || !context || context.kind === "edit" && !this.editTask) return;
        this.cancelOptionsRequest();
        const scope = this.scopeVersion;
        const version = this.optionVersion;
        const token = this.login.getToken(), identity = assessmentTaskSessionIdentity(this.login.getUserSnapshot());
        const sameSession = () => token === this.login.getToken() && identity === assessmentTaskSessionIdentity(this.login.getUserSnapshot());
        const previousRequirements = this.requirementPage;
        const loadFullRequirements = !this.isRequirementLocked &&
            (mode === "replace" || !previousRequirements.items.length);
        const requirementLimit = loadFullRequirements ? REQUIREMENT_OPTION_LIMIT : 1;
        const assigneeOffset = this.assigneePage.offset;
        this.optionsLoading = true;
        this.optionsError = null;
        const query = {
            requirementOffset: 0, requirementLimit,
            assigneeOffset, assigneeLimit: ASSIGNEE_OPTION_LIMIT,
            assigneeSearch: this.assigneePage.search
        };
        const request: Observable<AssessmentTaskCreationOptions | AssessmentTaskEditOptions> = context.kind === "edit"
            ? this.tasks.getEditOptions(context.taskId, query)
            : this.tasks.getCreationOptions(assessmentId, query);
        const selectedIds = [...this.form.controls.assigneeUserIds.getRawValue()];
        const selectedReads = selectedIds.map((assigneeUserId) => {
            const selectedQuery = { assigneeUserId, assigneeOffset: 0, assigneeLimit: 1, requirementLimit: 1 };
            return context.kind === "edit" ? this.tasks.getEditOptions(context.taskId, selectedQuery)
                : this.tasks.getCreationOptions(assessmentId, selectedQuery);
        });
        this.optionsRequest = forkJoin({ options: request, selected: selectedReads.length ? forkJoin(selectedReads) : of([]) }).subscribe({
            next: ({ options, selected }) => {
                if (!sameSession() || scope !== this.scopeVersion || version !== this.optionVersion || !this.state.isOpen ||
                    this.form.controls.productLicenseId.getRawValue() !== assessmentId) return;
                if (options.productLicenseId !== assessmentId || context.kind === "edit" &&
                    (!("canEdit" in options) || !options.canEdit || options.revision !== this.editTask?.revision)) {
                    this.optionsLoading = false;
                    this.optionsError = "Task choices changed. Reload the current task before saving.";
                    this.saveConflict = true;
                    this.maybeFinishInitialLoad();
                    return;
                }
                if (!sameValues(selectedIds, this.form.controls.assigneeUserIds.getRawValue())) {
                    this.loadOptions("preserve");
                    return;
                }
                if (selected.some((result, index) => result.productLicenseId !== assessmentId ||
                    result.assignees.total !== 1 || !result.assignees.items.some((item) => item.userId === selectedIds[index]) ||
                    context.kind === "edit" && (!("canEdit" in result) || !result.canEdit || result.revision !== this.editTask?.revision))) {
                    this.optionsLoading = false;
                    this.optionsError = "A selected task assignee is no longer eligible. Your draft is preserved; remove that selection or reopen the task before saving.";
                    this.maybeFinishInitialLoad();
                    return;
                }
                for (const result of selected) for (const member of result.assignees.items) this.selectedAssignees.set(member.userId, member);
                if (options.requirements.offset !== 0 ||
                    loadFullRequirements && (options.requirements.total > REQUIREMENT_OPTION_LIMIT ||
                        options.requirements.items.length !== options.requirements.total) ||
                    mode === "preserve" && previousRequirements.items.length > 0 &&
                    options.requirements.total !== previousRequirements.total) {
                    this.optionsLoading = false;
                    this.optionsError = loadFullRequirements &&
                        options.requirements.total > REQUIREMENT_OPTION_LIMIT
                        ? "This assessment has more than 500 requirements; the task choices cannot be loaded."
                        : "Task choices changed. Reload the task choices before saving.";
                    this.maybeFinishInitialLoad();
                    return;
                }
                if (mode === "preserve" && previousRequirements.items.length) {
                    this.requirementPage = { ...previousRequirements, search: this.requirementPage.search };
                } else {
                    this.requirementPage = { ...options.requirements, search: this.requirementPage.search };
                }
                this.assigneePage = { ...options.assignees, search: this.assigneePage.search };
                for (const item of options.requirements.items) {
                    const key = requirementKey(item);
                    if (this.form.controls.requirementKeys.value.includes(key)) this.selectedRequirements.set(key, item);
                }
                for (const item of options.assignees.items) {
                    if (this.form.controls.assigneeUserIds.value.includes(item.userId)) {
                        this.selectedAssignees.set(item.userId, item);
                    }
                }
                this.optionsLoading = false;
                this.refreshInvitationCapability();
                this.maybeFinishInitialLoad();
            },
            error: () => {
                if (!sameSession() || scope !== this.scopeVersion || version !== this.optionVersion || !this.state.isOpen) return;
                this.optionsLoading = false;
                this.canInviteAssessmentUser = false;
                this.optionsError = "Unable to load task choices for this assessment.";
                this.maybeFinishInitialLoad();
            }
        });
    }

    private resetScope(): void {
        this.assignmentScopeRefreshPending = false;
        this.formRevision += 1;
        this.userOriginChanges.next();
        this.childUserRequest?.unsubscribe();
        this.returnedUserRequest?.unsubscribe();
        this.invitationCapabilityRequest?.unsubscribe();
        this.canInviteAssessmentUser = false;
        this.childUserDialogOpen = false;
        this.newUserReturnPending = false;
        this.assigneeDropdownOpen = false;
        if (this.openTimer !== null) {
            clearTimeout(this.openTimer);
            this.openTimer = null;
        }
        this.scopeVersion += 1;
        this.cancelOptionsRequest();
        this.detailRequest?.unsubscribe();
        this.detailRequest = null;
        this.saveRequest?.unsubscribe();
        this.saveRequest = null;
        this.requiredEvidenceAccessRequest?.unsubscribe();
        this.requiredEvidenceAccessRequest = null;
        this.requiredEvidenceLaunchRequest?.unsubscribe();
        this.requiredEvidenceLaunchRequest = null;
        this.requiredEvidenceAvailable = false;
        this.requiredEvidenceLaunchPending = false;
        this.licensesRequest?.unsubscribe();
        this.licensesRequest = null;
        this.licenseVersion += 1;
        this.licenses = [];
        this.assessmentChoicesCache = null;
        this.requirementGroupsCache = null;
        this.assigneeChoicesCache = null;
        this.selectedRequirements.clear();
        this.selectedAssignees.clear();
        this.requirementPage = emptyPage();
        this.assigneePage = emptyPage();
        this.optionsLoading = false;
        this.optionsError = null;
        this.assessmentsLoading = false;
        this.assessmentsError = null;
        this.assessmentSearch = "";
        this.originalDueDate = null;
        this.dueDatePickerValue = null;
        this.originalDescription = "";
        this.originalRequirementKeys = [];
        this.originalAssigneeIds = [];
        this.editTask = null;
        this.editLoading = false;
        this.editError = null;
        this.saving = false;
        this.saveError = null;
        this.saveConflict = false;
        this.initialLoadPending = false;
        this.form.controls.productLicenseId.enable({ emitEvent: false });
        this.form.controls.description.enable({ emitEvent: false });
        this.form.controls.dueDate.enable({ emitEvent: false });
        this.form.controls.assigneeUserIds.enable({ emitEvent: false });
        this.form.controls.requirementKeys.enable({ emitEvent: false });
        this.form.controls.assigneeUserIds.setValidators(taskSelectionValidator(TASK_ASSIGNEE_MAX_COUNT));
        this.form.reset({
            productLicenseId: null, requirementKeys: [], assigneeUserIds: [],
            description: "", dueDate: null, status: "needs_review"
        }, { emitEvent: false });
    }

    private cancelOptionsRequest(): void {
        this.optionVersion += 1;
        this.optionsRequest?.unsubscribe();
        this.optionsRequest = null;
    }

    private maybeFinishInitialLoad(): void {
        if (this.initialLoadPending && !this.assessmentsLoading && !this.optionsLoading && !this.editLoading) {
            this.initialLoadPending = false;
        }
    }

    private selectedRequirementRefs(keys: readonly string[]): AssessmentTaskRequirementRef[] | null {
        const result: AssessmentTaskRequirementRef[] = [];
        for (const key of keys) {
            const selected = this.selectedRequirements.get(key);
            if (!selected) return null;
            result.push({ sectionKey: selected.sectionKey, requirementKey: selected.requirementKey });
        }
        return result;
    }

    private closeAfterSave(): void {
        this.saving = false;
        this.launch.close();
    }

    private handleSaveError(error: unknown, scope: number): void {
        if (scope !== this.scopeVersion || !this.state.isOpen) return;
        this.saving = false;
        this.refreshAssignmentScopeWhenReady();
        const code = error instanceof HttpErrorResponse && typeof error.error?.code === "string"
            ? error.error.code : null;
        this.saveConflict = code === "TASK_CONFLICT";
        this.saveError = this.saveConflict
            ? "This task changed while you were editing. Your draft is still here. Reload the task to start from its latest version."
            : code === "TASK_ASSESSMENT_UNAVAILABLE" || code === "TASK_NOT_FOUND"
                ? "This assessment or task is no longer available for this change."
                : code === "TASK_INVALID_INPUT"
                    ? "The task choices or due date changed. Review them and try again."
                    : "Unable to save this task. Please try again.";
    }
}
